import { FormControl } from '@angular/forms';
import { IAssessment } from '@models/admin-benchmark';
import { Classroom } from '@models/classroom';
import { IClassroomProductDescription } from '@models/classroom-products';
import { KeyValuePair } from '@models/key-value-pair';
import { SchoolLicense } from '@models/license/school-license';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { School } from '@models/school';
import { Topic } from '@models/topic';
import { Capabilities } from '@shared/enums/capabilities';
import { ZbChartBar } from './zbchart-report/zbchart-report.model';

/**
 * Contains functions that help streamline the Reports Module
 */
export namespace BrandedReportsHelpers {
  export function getClassroomsWithCapabilities(classrooms: Classroom[], capability: Capabilities): Classroom[] {
    return classrooms.filter(classroom => classroom
      .profile?.spellingProfiles?.some(profile => profile
        .classroomCapabilities?.includes(capability)
      )
      || classroom.profile?.gumProfiles?.some(profile => profile
        .classroomCapabilities?.includes(capability)
      )
      || classroom.profile?.superkidsProfiles?.some(profile => profile
        .classroomCapabilities?.includes(capability)
      )
    );
  }

  export function getDefaultClassroomId(classrooms: Classroom[], classroomIdControl: FormControl): null | string {
    let defaultValue = classrooms.length === 1
      ? classrooms[0].classroomId
      : null;

    if (!defaultValue && BrandedReportsHelpers.findClassroomById(classrooms, classroomIdControl.value)) {
      defaultValue = classroomIdControl.value;
    }
    return defaultValue;
  }

  export function findClassroomById(classrooms: Classroom[], classroomId?: string): Classroom | undefined {
    if (classroomId) {
      return classrooms.find(c => c?.classroomId === classroomId);
    }
    return undefined;
  }

  export function findSchoolYearRangeByYear(schoolYears: KeyValuePair[], schoolYear: string): string {
    return schoolYears?.find(year => year.key.toString() === schoolYear)?.value;
  }

  export function findSchoolNameByEducationalUnitId(schools: School[], educationUnitId: string): string {
    return schools.find(school => school.educationalUnitId === educationUnitId)?.name;
  }

  export function findAssessmentNameByKey(assessments: IAssessment[], assessmentKey: string) {
    return assessments.find(assessment => assessment.assessmentKey === assessmentKey)?.assessmentName;
  }

  export function findGroupLabelByValue(viewGroupOptions: { value: string, label: string }[], value: string): string {
    return viewGroupOptions.find(group => group.value === value)?.label;
  }

  export function findTopicByKey(topics: Topic[], topicKey: string): Topic {
    return topics.find(topic => topic.topicKey === topicKey);
  }

  export function getDefaultSkuId(schoolLicenses: SchoolLicense[], productSkuIdControl: FormControl): null | string {
    let defaultValue = schoolLicenses.length === 1
      ? schoolLicenses[0].skuId
      : null;

    if (!defaultValue && BrandedReportsHelpers.findProductById(schoolLicenses, productSkuIdControl.value)) {
      defaultValue = productSkuIdControl.value;
    }

    return defaultValue;
  }

  export function findProductById(schoolLicenses: SchoolLicense[], skuId?: string): SchoolLicense | undefined {
    if (skuId) {
      return schoolLicenses.find(p => p?.skuId === skuId);
    }
    return undefined;
  }

  /** Clears values for each heading */
  export function resetPrintableReportHeadings(
    printableReportHeadings: IPrintableReportHeading[]): IPrintableReportHeading[] {
    return printableReportHeadings.map(heading => ({ ...heading, value: '' }));
  }

  /**
   * Groups report results into arrays that are limited in length
   * by how many results should be included per group.
   *
   * Useful for printing results on separate pages to avoid a row being broken up on separate pages.
   *
   * @param {[]} reportResults
   * @param {string} entriesPerGroup - the limit for how many results should be included in each group
   * @returns {[]} Array of results grouped in arrays limited in length by entriesPerGroup
  */
  export function groupReportResultsByAmount(reportResults: any[], entriesPerGroup: number): [][] {
    const resultPages = [];
    for (let i = 0; i < reportResults?.length; i += entriesPerGroup) {
      resultPages.push(reportResults.slice(i, i + entriesPerGroup));
    }

    return resultPages;
  }

  export function findProductByDescription(
    schoolLicenses: SchoolLicense[] | IClassroomProductDescription[],
    description?: string
  ): SchoolLicense | IClassroomProductDescription | undefined {
    if (description) {
      return schoolLicenses.find(p => p?.description === description);
    }
    return undefined;
  }

  /**
   * Used to get a license from a variant, only considers variant, currently used by sk26 reports
   *
   * @param schoolLicenses
   * @param variant
   * @returns
   */
  export function findProductByVariantType(
    schoolLicenses: SchoolLicense[],
    variant?: string): SchoolLicense | undefined {
    if (variant) {
      return schoolLicenses.find(p => p?.variant === variant);
    }
    return undefined;
  }

  export function getProductsWithCapabilities(products: SchoolLicense[], capability: Capabilities): SchoolLicense[] {
    return products
      .filter(product => product.classroomCapabilities.split(',').includes(capability));
  }

  export function getClassroomsWithAdminCapabilities(classrooms: Classroom[], capability: Capabilities): Classroom[] {
    return classrooms.filter(classroom => classroom
      .licenses?.some(license => license
        .adminCapabilities?.includes(capability)
      )
    );
  }

  export function zbChartTooltipFormatter(data: ZbChartBar): string {
    let tooltipHtml = '<div class="floating-tooltip-body">';

    tooltipHtml += `<div>${data.label}</div>`;

    if (!data.fullLengthButton) {
      data.values.forEach((value) => {
        const styles = [
          `border: 2px ${value.borderStyle ?? 'solid'} ${value.borderColor ?? 'transparent'}`,
          `background-color:${value.color}`
        ].join(';');
        tooltipHtml += `
              <div class="tooltip-line">
                <span class="tooltip-color-patch" style="${styles};"></span>
                <span>${value.tooltipLabel}</span>
                <span class="tooltip-value">${value.tooltipValue ?? '-'}</span>
              </div>
            `;
      });
    }

    tooltipHtml += '</div>';

    return tooltipHtml;
  }
}
